import {Box, Text, TextField, TextInput, Typography} from '@gwc/main'
import {flatten, noop} from '@yandex-delivery/utils'
import {SpinnerIcon} from '@yandex-delivery/blocks'
import clsx from 'clsx'
import {parseDigit, templateFormatter, templateParser} from 'input-format'
import InputFormat from 'input-format/react'
import React from 'react'

import {ClearableTrail, slotFromArray, useSlots} from '../slot'
import {inputClasses, spinner} from './Input.classes'
import {InputProps as Props} from './types'

function InputBasev2({
  variant = 'outline',
  startIcon,
  startAdornment,
  lead: leadProp,
  endIcon,
  endAdornment,
  trail: trailProp,
  helperText,
  'data-testid': dataTestId,
  label,
  value,
  clearable,
  autoComplete = 'new-password',
  name,
  size,
  placeholder,
  error,
  disabled,
  readOnly,
  children,
  onChange,
  onBlur,
  color,
  mask,
  className,
  onClear,
  pending,
  getPendingPlaceholder,
  ...props
}: Props) {
  const trailProps = flatten([trailProp || endIcon || endAdornment])

  const {lead, trail} = useSlots({
    lead: leadProp || startIcon || startAdornment,
    trail: slotFromArray([
      clearable && (
        <ClearableTrail
          isVisible={!!value}
          onClear={() => {
            onClear?.()
            onChange?.('')
          }}
        />
      ),
      ...trailProps,
    ]),
  })

  return (
    <>
      <TextField
        autoComplete={autoComplete}
        className={clsx(inputClasses.textField, {[inputClasses.pending]: pending}, className)}
        color={color}
        disabled={pending || disabled}
        error={error}
        name={name}
        onBlur={onBlur}
        onChange={!readOnly ? onChange : noop}
        placeholder={pending && getPendingPlaceholder? getPendingPlaceholder(String(value)) : placeholder}
        readOnly={readOnly}
        size={size}
        // @ts-expect-error компонент принимает только 'on' | 'off', а надо передавать 'new-password'
        value={pending && getPendingPlaceholder? '' : String(value)}
        variant={variant}
      >
        {({inputProps, labelProps}) => (
          <>
            {lead}
            <Text data-testid={dataTestId}>
              {label && <Typography {...labelProps}>{label}</Typography>}
              {!!mask ? (
                <InputFormat
                  data-error={error}
                  format={templateFormatter(mask)}
                  parse={templateParser(mask, parseDigit)}
                  {...inputProps}
                  // переопределяем onChange из inputProps потому что InputFormat за нас достает значение
                  className={inputProps.className}
                  // ввод телефонов в RTL довольно плохо работает as is, поэтому адаптируемся
                  onChange={onChange}
                  {...props}
                />
              ) : (
                <TextInput {...inputProps} className={inputProps.className} data-error={error} {...props} />
              )}
            </Text>
            {!pending && trail}
            {pending && <SpinnerIcon className={spinner} />}
          </>
        )}
      </TextField>
      {/* делаем свой helperText из-за того что GWC-шный абсолютно позиционирован, в дальнейшем уберем */}
      {helperText && (
        <Box pt="xs">
          <Typography color={error ? 'error' : 'minor'} data-e2e="required-field" variant="caption1">
            {helperText}
          </Typography>
        </Box>
      )}
    </>
  )
}

/**
 * Компонент новый и целевой - использовать осторожно, но в итоге на эту версию планируем перевести всё. Для использования делаем импорт алиас:
 * `import {Inputv2 as Input} from '@yandex-delivery/blocks'`
 * Когда компонент стабилизируется, нужно будет легко и безболезненно переделать все такие места.
 */
export const Inputv2 = React.memo(InputBasev2)

export default Inputv2
